import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './Investordisclaimermodal.css';

const InvestorDisclaimerModal = ({ show, onAcknowledge }) => {
  const [checked, setChecked] = useState(false);
  const checkboxRef = useRef(null);

  // Move focus into the modal when it opens
  useEffect(() => {
    if (show && checkboxRef.current) {
      const timer = setTimeout(() => {
        checkboxRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Reset checkbox when modal re-opens
  useEffect(() => {
    if (show) setChecked(false);
  }, [show]);

  const handleClose = useCallback(() => {
    onAcknowledge();
  }, [onAcknowledge]);

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={true}
      onHide={handleClose}
      enforceFocus={true}
      restoreFocus={true}
      aria-labelledby="investor-disclaimer-title"
      aria-describedby="investor-disclaimer-body"
      className="investor-disclaimer-modal"
      dialogClassName="investor-disclaimer-dialog"
      contentClassName="rounded-[1.5rem] overflow-hidden border-0"
    >
      {/* Header */}
      <div className="investor-disclaimer-header bg-[#1e6091] px-6 py-6 md:px-8 md:py-7">
        <div className="flex items-center gap-3">
          <svg className="w-8 h-8 text-amber-300 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2 1 21h22L12 2zm0 4.5 7.5 13h-15L12 6.5zM11 10v5h2v-5h-2zm0 6.5v2h2v-2h-2z" />
          </svg>
          <h2 id="investor-disclaimer-title" className="text-white text-xl md:text-2xl font-bold m-0">
            Important Investor Disclaimer
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="ml-auto bg-transparent border-0 text-white text-2xl leading-none p-1 hover:text-amber-300 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 focus:ring-offset-[#1e6091] rounded"
            aria-label="Close disclaimer"
          >
            &times;
          </button>
        </div>
        <p className="text-blue-100 text-xs md:text-sm font-semibold tracking-widest uppercase mt-3 mb-0">
          Please read carefully before proceeding
        </p>
      </div>

      {/* Scrollable body */}
      <div
        id="investor-disclaimer-body"
        className="investor-disclaimer-body bg-white px-6 py-6 md:px-8 md:py-7 max-h-[55vh] overflow-y-auto"
      >
        <p className="text-zinc-700 text-sm md:text-base leading-relaxed mb-6">
          By clicking &ldquo;I Acknowledge&rdquo;, you confirm that you have read, understood, and
          accepted the disclosures mentioned below.
        </p>

        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden="true">📋</span>
            <h3 className="text-[#1e6091] font-bold text-base md:text-lg m-0">
              General Risk Warning
            </h3>
          </div>
          <ul className="list-disc pl-5 space-y-2 text-zinc-700 text-sm md:text-base leading-relaxed">
            <li>Investments in securities markets are subject to market risks. Read all related documents carefully before investing.</li>
            <li>Securities displayed on this website, unless forming part of a Research Report or specific recommendation, are shown for illustration and <strong>knowledge</strong> purposes only and are not recommendatory in nature.</li>
            <li>Registration granted by SEBI and certification from NISM do not guarantee the performance of the intermediary or assure returns to investors.</li>
            <li>We do not provide any assured return, guaranteed profit, or capital protection services.</li>
            <li>Trading and investing in securities, derivatives, commodities, and related instruments involve substantial risk, including the possible loss of entire capital invested.</li>
            <li>Investors should carefully consider their financial situation, investment objectives, risk appetite, and consult their advisers before making investment decisions.</li>
          </ul>
        </section>

        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden="true">⚠️</span>
            <h3 className="text-[#1e6091] font-bold text-base md:text-lg m-0">
              SEBI Study on Derivatives Trading
            </h3>
          </div>
          <p className="text-zinc-700 text-sm md:text-base leading-relaxed mb-2">
            As per SEBI&rsquo;s study on individual traders in the Equity Futures &amp; Options segment:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-zinc-700 text-sm md:text-base leading-relaxed">
            <li>9 out of 10 individual traders incurred net losses.</li>
            <li>The average net loss of loss-making traders was approximately ₹50,000.</li>
            <li>Transaction costs significantly impacted overall profitability.</li>
            <li>Even profitable traders incurred substantial costs towards brokerage, taxes, and other charges.</li>
          </ul>
        </section>

        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden="true">🛡️</span>
            <h3 className="text-[#1e6091] font-bold text-base md:text-lg m-0">
              Investor Acknowledgement
            </h3>
          </div>
          <p className="text-zinc-700 text-sm md:text-base leading-relaxed mb-2">
            By proceeding further, you acknowledge that:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-zinc-700 text-sm md:text-base leading-relaxed">
            <li>You understand the risks associated with trading and investing in securities markets.</li>
            <li>You are solely responsible for your investment and trading decisions.</li>
            <li>Past performance does not guarantee future results.</li>
            <li>All investments are subject to market risk and actual returns may vary.</li>
            <li>You have read the Terms &amp; Conditions, Disclosures, Disclaimer, Privacy Policy, and Refund Policy etc. available on this website.</li>
          </ul>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden="true">⚠️</span>
            <h3 className="text-[#1e6091] font-bold text-base md:text-lg m-0">
              Final Confirmation
            </h3>
          </div>
          <p className="text-zinc-700 text-sm md:text-base leading-relaxed mb-2">
            By clicking &ldquo;I Acknowledge&rdquo;, you confirm that:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-zinc-700 text-sm md:text-base leading-relaxed">
            <li>You have read and understood the above disclosures.</li>
            <li>You understand that investments are subject to market risk.</li>
            <li>You are proceeding based on your own judgment and discretion.</li>
            <li>You acknowledge and accept the risks associated with investing and trading in securities markets.</li>
          </ul>
        </section>
      </div>

      {/* Footer — always visible, not under floating buttons */}
      <div className="investor-disclaimer-footer bg-white px-6 pb-6 pt-2 md:px-8 md:pb-7 border-t border-zinc-100">
        <label className="flex items-start gap-2 mb-4 cursor-pointer select-none">
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#1e6091] flex-shrink-0"
            aria-label="I have read and understood the disclosures above and wish to proceed"
          />
          <span className="text-xs md:text-sm text-zinc-600 leading-relaxed">
            I have read and understood the disclosures above and wish to proceed.
          </span>
        </label>

        <Button
          onClick={onAcknowledge}
          disabled={!checked}
          className="w-full flex items-center justify-center gap-2 !bg-[#1e6091] hover:!bg-[#185076] !text-white !border-none font-bold text-base md:text-lg py-3.5 rounded-xl shadow-[0_10px_20px_rgba(30,96,145,0.2)] hover:shadow-[0_15px_30px_rgba(30,96,145,0.35)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          I Acknowledge &amp; Proceed
        </Button>
      </div>
    </Modal>
  );
};

export default InvestorDisclaimerModal;
